import { useAssignBriefPoc } from "@entities/brief/lib/hooks/useAssignBriefPoc";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useAssignOperationsPoc } from "@entities/campaigns/lib/hooks/useAssignOperationsPoc";
import AdminCampaignDetailsChangeModal from "@features/AdminCampaignDetailsChange";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { ToastService } from "@shared/lib/services";
import { IPlanningOrPricing } from "@shared/lib/types/brief";
import { BriefCampaignInf } from "@shared/lib/types/briefCampains";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { formatData } from "@shared/lib/utils/formatData";
import { DeliverablePillSummary, Select } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { Empty, Flex } from "antd";
import { ChevronDown, ChevronUp, IndianRupee, LayoutDashboard } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CampaignAlterationHistory } from "./CampaignAlterationHistory";
import styles from "./ExecutionCommercialsSummary.module.scss";

type ConfirmationMailStatus = "sent" | "not_sent" | "pending";

interface ExecutionCommercialsSummaryProps {
  briefInfluencer: IBriefInfluencerV2;
  campaignInfluencer?: BriefCampaignInf;
  finalBrandCost: number;
  finalInfluencerCost: number;
  defaultExpanded?: boolean;
  onPocUpdated?: () => void;
}

const formatCommercial = (cost?: number) => {
  if (cost === undefined || cost === null) return null;
  return `₹${formatData(cost, "0.00")}`;
};

const buildPocOptions = (allocatedTo?: IPlanningOrPricing["allocatedTo"]) =>
  allocatedTo?.map((item) => ({
    label: `${item.user.firstName} ${item.user.lastName}`.trim(),
    value: item.userId,
  })) ?? [];

const getConfirmationMailStatus = (
  briefInfluencer: IBriefInfluencerV2
): { status: ConfirmationMailStatus; label: string } => {
  if (briefInfluencer.isConfirmed) {
    return { status: "sent", label: "Confirmed" };
  }
  if (briefInfluencer.isConfirmationSent) {
    return { status: "pending", label: "Confirmation Sent" };
  }
  if (briefInfluencer.status === "Purchased") {
    return { status: "not_sent", label: "Not Sent" };
  }
  return { status: "pending", label: "Pending" };
};

const getStatusPillClass = (status: ConfirmationMailStatus) => {
  switch (status) {
    case "sent":
      return styles.statusSent;
    case "pending":
      return styles.statusPending;
    default:
      return styles.statusNotSent;
  }
};

export function ExecutionCommercialsSummary({
  briefInfluencer,
  campaignInfluencer,
  defaultExpanded = false,
  onPocUpdated,
  finalBrandCost,
  finalInfluencerCost
}: ExecutionCommercialsSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);

  const currentDeliverables = useMemo(() => {
    const list = briefInfluencer?.deliverableV2?.purchaseItems ?? [];
    return list.map((item) => {
      const platform = item.contentDeliverableConfigurator?.platform || "";
      const category = item.contentCategory || "";
      const subTypeStr = Object.values(item.contentSubType ?? {})
        .filter(Boolean)
        .join(", ");
      return [platform, category, subTypeStr].filter(Boolean).join(" - ") || "Deliverable";
    });
  }, [briefInfluencer.deliverableV2]);
  const briefId = briefInfluencer.briefId;

  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded, briefInfluencer._id]);

  const { brief } = useGetBrIefById(briefId);
  const {
    assignPricingPoc,
    isAssigning: isAssigningPricingPoc,
    isAssigned: isPricingPocAssigned,
  } = useAssignBriefPoc(briefId);
  const {
    assignOperationsPoc,
    isAssigning: isAssigningOpsPoc,
    isAssigned: isOpsPocAssigned,
  } = useAssignOperationsPoc(briefId);

  const pricingPocOptions = useMemo(
    () => buildPocOptions(brief?.pricing?.allocatedTo),
    [brief?.pricing?.allocatedTo]
  );
  const opsPocOptions = useMemo(
    () => buildPocOptions(brief?.operations?.allocatedTo),
    [brief?.operations?.allocatedTo]
  );

  const pricingPocUserId =
    briefInfluencer.assignedPricingPocV2?.assignedToUserId || undefined;
  const opsPocUserId =
    campaignInfluencer?.assignedOperationsPoc?.assignedTo?._id || undefined;

  useEffect(() => {
    if (isPricingPocAssigned) {
      ToastService.success("Pricing POC updated successfully");
      onPocUpdated?.();
    }
  }, [isPricingPocAssigned, onPocUpdated]);

  useEffect(() => {
    if (isOpsPocAssigned) {
      ToastService.success("Ops POC updated successfully");
      onPocUpdated?.();
    }
  }, [isOpsPocAssigned, onPocUpdated]);

  const handlePricingPocChange = (assignedToUserId: string) => {
    if (!assignedToUserId || assignedToUserId === pricingPocUserId) return;

    assignPricingPoc({
      briefInfluencerId: [briefInfluencer._id],
      assignedToUserId,
    });
  };

  const handleOpsPocChange = (assignedToUserId: string) => {
    if (!assignedToUserId || !campaignInfluencer?._id) return;
    if (assignedToUserId === opsPocUserId) return;

    assignOperationsPoc({
      briefInfluencerId: [campaignInfluencer._id],
      assignedToUserId,
    });
  };

  const pricingNotes = briefInfluencer.pricingNotes?.trim() || null;
  const confirmationMail = getConfirmationMailStatus(briefInfluencer);
  const goLiveTimeline = briefInfluencer.paymentTerms?.trim() || "";
  const confirmationNotes = briefInfluencer.confirmationNotes?.trim() || null;

  return (
    <div className={styles.summary}>
      <button
        type="button"
        onClick={() => setIsExpanded((value) => !value)}
        className={styles.toggleButton}
      >
        <h4 className={styles.title}>
          <LayoutDashboard size={14} className={styles.titleIcon} />
          Campaign Details
        </h4>
        <span className={styles.chevron}>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>

      {isExpanded ? (
        <div className={styles.content}>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Pricing POC</p>
            <div className={styles.pocRow}>
              <Select
                className={styles.pocSelect}
                showSearch
                placeholder="Select pricing POC"
                value={pricingPocUserId}
                options={pricingPocOptions}
                loading={isAssigningPricingPoc}
                disabled={
                  !pricingPocOptions.length ||
                  isAssigningPricingPoc ||
                  isAssigningOpsPoc
                }
                optionFilterProp="label"
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={handlePricingPocChange}
              />
              {pricingNotes ? (
                <p className={styles.pricingNotes} title={pricingNotes}>
                  {pricingNotes}
                </p>
              ) : null}
            </div>
          </div>

          <div className={styles.row}>
            <p className={styles.rowLabel}>Ops POC</p>
            <Select
              className={styles.pocSelect}
              showSearch
              placeholder="Select ops POC"
              value={opsPocUserId}
              options={opsPocOptions}
              loading={isAssigningOpsPoc}
              disabled={
                !opsPocOptions.length ||
                !campaignInfluencer?._id ||
                isAssigningPricingPoc ||
                isAssigningOpsPoc
              }
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={handleOpsPocChange}
            />
          </div>

          <div className={styles.row}>
            <div className={styles.rowHeader}>
              <p className={`${styles.rowLabel} ${styles.rowHeaderLabel}`}>
                Influencer commercial
              </p>
              <BriefTimelinePopOver
                briefInfluencer={briefInfluencer}
                type="influencer"
              />
            </div>
            {finalInfluencerCost !== undefined ? (
              <>
                <div className={styles.commercialValue}>
                  ₹{finalInfluencerCost ?? 0}
                </div>
                {/* <InrAmountWordsLine
                  value={briefInfluencer.influencerCost?.cost}
                  className={styles.amountInWords}
                /> */}
              </>
            ) : (
              <p className={styles.emptyValue}>Not set</p>
            )}
          </div>

          <div className={styles.row}>
            <div className={styles.rowHeader}>
              <p className={`${styles.rowLabel} ${styles.rowHeaderLabel}`}>
                Brand commercial
              </p>
              <BriefTimelinePopOver
                briefInfluencer={briefInfluencer}
                type="brand"
              />
            </div>
            {finalBrandCost !== undefined ? (
              <>
                <div className={styles.commercialValue}>
                  ₹{finalBrandCost ?? 0}
                </div>
                {/* <InrAmountWordsLine
                  value={briefInfluencer.brandCost?.cost}
                  className={styles.amountInWords}
                /> */}
              </>
            ) : (
              <p className={styles.emptyValue}>Not set</p>
            )}
          </div>

          <div className={styles.row}>
            <p className={styles.rowLabel}>Deliverables</p>
            <div className={styles.deliverableContent}>
              {campaignInfluencer?.purchaseItems && campaignInfluencer?.purchaseItems?.length > 0 ? (
                <Flex align="center">
                {/* <DeliverablePillSummary deliverable={{
                  briefId: briefInfluencer.briefId,
                  purchaseItems: briefInfluencer?.deliverableV2?.purchaseItems
                }}/>
                → */}
                {campaignInfluencer?.purchaseItems?.length > 0 ? <DeliverablePillSummary deliverable={{
                  briefId: briefInfluencer.briefId,
                  purchaseItems: campaignInfluencer?.purchaseItems
                }}/> : <Empty description="Deliverables not found" style={{width: "100%"}}/>}
                </Flex>
              ) : (
                <Empty
                  description="Deliverables not found"
                  style={{width: "100%"}}
                />
              )}
            </div>
          </div>

          <div className={styles.row}>
            <p className={styles.rowLabel}>Confirmation mail status</p>
            <span
              className={`${styles.statusPill} ${getStatusPillClass(confirmationMail.status)}`}
            >
              {confirmationMail.label}
            </span>
          </div>

          <div className={styles.row}>
            <p className={styles.rowLabel}>Payment terms</p>
            <p className={styles.bodyText}>{goLiveTimeline}</p>
          </div>

          <div className={styles.row}>
            <p className={styles.rowLabel}>Special notes</p>
            {confirmationNotes ? (
              <p className={styles.notesText}>{confirmationNotes}</p>
            ) : (
              <p className={styles.emptyValue}>—</p>
            )}
          </div>
          {campaignInfluencer ? <>
          <div className={styles.row}>
            <p className={styles.rowLabel}>Alteration Request History</p>
            <CampaignAlterationHistory
              campaignInfluencerId={campaignInfluencer?._id || briefInfluencer._id}
              briefId={briefId}
              campaignInfluencer={campaignInfluencer}
            />
          </div>
          <div className={styles.row}>
            <Button
              icon={<IndianRupee size={14}/>}
              style={{width: "100%"}}
              onClick={() => setIsChangeModalOpen(true)}
            >
              Change Campaign Details
            </Button>
          </div>
          </> : null}
        </div>
      ) : null}

      <AdminCampaignDetailsChangeModal
        isOpen={isChangeModalOpen}
        onClose={() => setIsChangeModalOpen(false)}
        creatorName={
          briefInfluencer.influencerName ||
          (briefInfluencer.influencer
            ? `${briefInfluencer.influencer.firstName} ${briefInfluencer.influencer.lastName}`.trim()
            : "Creator")
        }
        // baseInfluencerCommercial={briefInfluencer.influencerCost?.cost ?? null}
        baseBrandCommercial={briefInfluencer.brandCost?.cost ?? null}
        currentDeliverables={currentDeliverables}
        briefId={briefId}
        campaignInfluencerId={campaignInfluencer?._id || briefInfluencer._id}
      />
    </div>
  );
}
